import { useState, useEffect } from 'react';
import API, { formatError } from '../api/client';
import { Plus, Droplets, Trash2, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PatientSelect from '../components/PatientSelect';

const EMPTY_FORM = {
  patient_id: '', date: new Date().toISOString().split('T')[0],
  creatinine: '', dfg: '', uree: '', proteinurie: '', albuminurie: '', kaliemie: '',
  tension_arterielle: '', poids: '', traitement: '', complications: '', objectifs: '', notes: ''
};

// Stade IRC (KDIGO) d'après le DFG (mL/min/1.73m²).
function stadeInfo(dfg) {
  const v = parseFloat(dfg);
  if (isNaN(v)) return null;
  if (v >= 90) return ['G1 — normal', 'bg-emerald-100 text-emerald-700'];
  if (v >= 60) return ['G2 — légère', 'bg-emerald-100 text-emerald-700'];
  if (v >= 45) return ['G3A — modérée', 'bg-amber-100 text-amber-700'];
  if (v >= 30) return ['G3B — modérée à sévère', 'bg-orange-100 text-orange-700'];
  if (v >= 15) return ['G4 — sévère', 'bg-red-100 text-red-700'];
  return ['G5 — terminale', 'bg-red-200 text-red-800'];
}

export default function RenalPage() {
  const [records, setRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async (pid = selectedPatient) => {
    const params = pid ? { patient_id: pid } : {};
    const [r, p] = await Promise.all([
      API.get('/renal', { params }),
      API.get('/patients'),
    ]);
    setRecords(r.data);
    setPatients(p.data.data ?? p.data);
  };

  useEffect(() => { load(); }, []);

  const handlePatientChange = (pid) => {
    setSelectedPatient(pid);
    setForm(f => ({ ...f, patient_id: pid }));
    load(pid);
  };

  const currentStade = stadeInfo(form.dfg);

  const handleSave = async () => {
    setError('');
    if (!form.patient_id) { setError('Veuillez sélectionner un patient.'); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === '') payload[k] = null;
        else if (['creatinine', 'dfg', 'uree', 'albuminurie', 'kaliemie', 'poids'].includes(k)) payload[k] = parseFloat(v) || null;
        else payload[k] = v;
      });
      await API.post('/renal', payload);
      setShowForm(false);
      setForm({ ...EMPTY_FORM, patient_id: selectedPatient });
      load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet enregistrement ?')) return;
    await API.delete(`/renal/${id}`);
    load();
  };

  const chartData = [...records].reverse().map(r => ({
    date: r.date,
    'DFG (mL/min)': r.dfg,
    'Créatinine (mg/L)': r.creatinine,
  }));

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );

  const inp = (field, type = 'text', placeholder = '') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500" />
  );

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="renal-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Droplets size={22} className="text-cyan-600" />
            Module Néphrologie
          </h1>
          <p className="text-stone-500 text-sm mt-0.5">Suivi de la fonction rénale &amp; insuffisance rénale chronique (IRC)</p>
        </div>
        <button data-testid="new-renal-record-button" onClick={() => { setShowForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors">
          <Plus size={17} /> Nouveau Suivi
        </button>
      </div>

      {/* Patient filter */}
      <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-4">
        <label className="text-sm font-medium text-stone-600 whitespace-nowrap">Filtrer par patient:</label>
        <PatientSelect
          testId="renal-patient-filter"
          className="flex-1 max-w-md"
          placeholder="Tous les patients — rechercher (nom, CIN, id)…"
          value={selectedPatient}
          patients={patients}
          allowClear
          onChange={(id) => handlePatientChange(id)}
        />
      </div>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h3 className="font-heading font-semibold text-stone-700 mb-4">Évolution de la fonction rénale</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#78716c' }} />
              <YAxis tick={{ fontSize: 11, fill: '#78716c' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: '8px', border: '1px solid #e7e5e4' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="DFG (mL/min)" stroke="#0891b2" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Créatinine (mg/L)" stroke="#e11d48" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="renal-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {['Date', 'Patient', 'Créat. (mg/L)', 'DFG', 'Stade IRC', 'Kaliémie', 'Protéinurie', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {records.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-stone-400 text-sm">
                  <Droplets size={36} className="text-stone-200 mx-auto mb-2" />
                  Aucun enregistrement
                </td></tr>
              )}
              {records.map(r => {
                const patient = patients.find(p => p.id === r.patient_id);
                const st = r.stade ? [r.stade, stadeInfo(r.dfg)?.[1] || 'bg-stone-100 text-stone-600'] : stadeInfo(r.dfg);
                return (
                  <tr key={r.id} className="hover:bg-stone-50/60 transition-colors" data-testid="renal-record-row">
                    <td className="px-4 py-3 text-sm text-stone-700 whitespace-nowrap">{r.date}</td>
                    <td className="px-4 py-3 text-sm font-medium text-stone-800">{patient ? `${patient.prenom} ${patient.nom}` : '—'}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{r.creatinine ?? '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {r.dfg != null ? (
                        <span className={parseFloat(r.dfg) < 60 ? 'text-red-600 font-semibold' : 'text-emerald-600 font-semibold'}>{r.dfg}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {st && <span className={`text-xs px-2 py-1 rounded-full font-medium ${st[1]}`}>{st[0]}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600">{r.kaliemie ? `${r.kaliemie} mmol/L` : '—'}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{r.proteinurie || '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(r.id)}
                        className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="renal-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
              <h2 className="font-heading font-semibold text-stone-800 text-lg">Nouveau Suivi Néphrologique</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <F label="Patient *">
                  <PatientSelect
                    value={form.patient_id}
                    patients={patients}
                    onChange={(id) => setForm(f => ({ ...f, patient_id: id }))}
                  />
                </F>
                <F label="Date">{inp('date', 'date')}</F>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Bilan rénal</h3>
                <div className="grid grid-cols-3 gap-4">
                  <F label="Créatininémie (mg/L)">{inp('creatinine', 'number', '12')}</F>
                  <F label="DFG (mL/min/1.73m²)">
                    <div>{inp('dfg', 'number', '90')}</div>
                    {currentStade && <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${currentStade[1]}`}>{currentStade[0]}</span>}
                  </F>
                  <F label="Urée (g/L)">{inp('uree', 'number', '0.30')}</F>
                  <F label="Kaliémie (mmol/L)">{inp('kaliemie', 'number', '4.2')}</F>
                  <F label="Albuminurie / RAC (mg/g)">{inp('albuminurie', 'number', '30')}</F>
                  <F label="Protéinurie">{inp('proteinurie', 'text', 'Ex: 0.5 g/24h, +, ++')}</F>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <F label="Tension artérielle">{inp('tension_arterielle', 'text', 'Ex: 130/80 mmHg')}</F>
                <F label="Poids (kg)">{inp('poids', 'number')}</F>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <F label="Traitement néphroprotecteur">
                  <textarea rows={2} placeholder="IEC/ARA2, restriction sodée, contrôle protéinurie, EPO, chélateurs du phosphore..." value={form.traitement}
                    onChange={e => setForm(f => ({ ...f, traitement: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 resize-none" />
                </F>
                <F label="Complications">
                  <textarea rows={2} placeholder="Anémie, troubles phosphocalciques, acidose, hyperkaliémie, surcharge..." value={form.complications}
                    onChange={e => setForm(f => ({ ...f, complications: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 resize-none" />
                </F>
                <F label="Objectifs / Conduite">
                  <textarea rows={2} placeholder="Cible TA, DFG, orientation néphrologue, préparation dialyse..." value={form.objectifs}
                    onChange={e => setForm(f => ({ ...f, objectifs: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 resize-none" />
                </F>
                <F label="Notes">
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 resize-none" />
                </F>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button data-testid="save-renal-button" onClick={handleSave} disabled={saving}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
